<template>
	<v-dialog
		:model-value="modelValue"
		:aria-labelledby="titleId"
		:max-width="isFullscreen ? undefined : resolvedMaxWidth"
		:width="isFullscreen ? undefined : resolvedWidth"
		:fullscreen="isFullscreen"
		:persistent="persistent"
		:content-class="[contentClass, { 'full-height': fullHeight }]"
		@update:model-value="onOverlayModelValueUpdate"
		@keydown.esc="requestClose"
		@click:outside="requestClose">
		<v-card
			data-test-id="dialog"
			class="pa-2 dialog-container w-100"
			:class="{ 'dialog-container--fullscreen': isFullscreen }"
			:rounded="isFullscreen ? 0 : 'xl'"
			elevation="8">
			<v-card-title
				class="text-h5 pa-4 dialog-header"
				:style="{ 'word-break': 'normal', 'white-space': 'normal' }"
				:class="{ 'shadow-bottom': hasScrolledDown }">
				<slot name="title" :title-id="titleId">
					<h5 :id="titleId" data-test-id="dialog-title">{{ title }}</h5>
				</slot>
			</v-card-title>
			<div
				ref="bodyContainer"
				class="dialog-body-container"
				:class="display.smAndDown.value && isLandscape ? 'landscape-dialog-body-container' : ''"
				@scroll="handleScroll">
				<v-card-text data-test-id="dialog-message" class="pa-4">
					<slot />
				</v-card-text>
			</div>
			<v-card-actions
				v-if="$slots.actions"
				class="pa-4 pb-2 d-flex align-center flex-wrap ga-2 dialog-footer"
				:class="[{ 'shadow-top': hasScrolledUp }, actionsLayoutClass]">
				<slot name="actions" />
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
	/** Standard max widths. A dialog should pick one of these rather than a one-off pixel value. */
	export const BASE_DIALOG_SIZES = { sm: 480, md: 640, lg: 960, xl: 1120 } as const;
	export type BaseDialogSize = keyof typeof BASE_DIALOG_SIZES;
	/** `end`: actions sit right. `between`: first action left, the rest right. `split`: two actions pushed apart, wrapping onto two rows when they can't keep a usable gap. */
	export type BaseDialogActionsLayout = "end" | "between" | "split";
</script>

<script lang="ts" setup>
	import { computed, nextTick, onBeforeUnmount, ref, useAttrs, useId, watch } from "vue";
	import { useDisplay } from "vuetify";

	/**
	 * The dialog frame every Contact dialog shares: v-model, a title, a scrolling body with
	 * scroll shadows, and an actions row. It owns no buttons and no copy; callers put those in
	 * the slots. Attributes it does not declare (z-index, scrim, opacity, contained, @after-leave)
	 * go straight through to the underlying v-dialog.
	 *
	 * Closing is always the parent's decision: an outside click or Escape emits
	 * `update:modelValue` with false (unless `persistent`), and the dialog stays open until the
	 * parent changes `modelValue`.
	 */
	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			title?: string;
			/** One of the standard widths. `maxWidth` wins when both are set. */
			size?: BaseDialogSize;
			maxWidth?: string | number;
			/** Desktop width. Defaults to the max width, so the dialog sits at its size and shrinks on narrower screens. */
			width?: string | number;
			persistent?: boolean;
			/** Lets the body grow to 90vh instead of 50vh before it scrolls. */
			fullHeight?: boolean;
			/** Fills the screen on phones and small tablets (Vuetify's smAndDown). */
			fullscreenOnMobile?: boolean;
			actionsLayout?: BaseDialogActionsLayout;
			contentClass?: string;
		}>(),
		{
			title: undefined,
			size: undefined,
			maxWidth: undefined,
			width: undefined,
			persistent: false,
			fullHeight: false,
			fullscreenOnMobile: false,
			actionsLayout: "end",
			contentClass: undefined
		}
	);
	const emit = defineEmits<{ "update:modelValue": [value: boolean] }>();
	const attrs = useAttrs();

	const display = useDisplay();
	const titleId = `dialog-title-${useId()}`;
	const hasScrolledDown = ref(false);
	const hasScrolledUp = ref(false);
	const bodyContainer = ref<HTMLElement | null>(null);

	const isFullscreen = computed(() => props.fullscreenOnMobile && display.smAndDown.value);
	const resolvedMaxWidth = computed(() => props.maxWidth ?? (props.size ? BASE_DIALOG_SIZES[props.size] : undefined));
	const resolvedWidth = computed(() => (display.smAndDown.value ? "90%" : (props.width ?? resolvedMaxWidth.value ?? "80%")));
	const actionsLayoutClass = computed(() => {
		if (props.actionsLayout === "between") return "justify-space-between";
		if (props.actionsLayout === "split") return "two-default-actions";
		return "justify-end";
	});

	// Phones in landscape get a shorter body. iOS fires orientationchange before its reflow is
	// complete, so the orientation is re-read ~100ms after the last event (LSP-16440). Both events
	// can fire for one rotation; the debounce collapses them. Mirrors admin's useOrientationChange,
	// except that the second event is the media query's own `change` rather than
	// `screen.orientation`, which iOS 15.0-15.1 lacks (the browser-support-floor gate rejects it).
	const landscapeQuery = "(orientation: landscape)";
	const landscapeMediaQuery = window.matchMedia(landscapeQuery);
	const isLandscape = ref(landscapeMediaQuery.matches);
	let orientationSettleTimer: ReturnType<typeof setTimeout> | null = null;
	const onOrientationChange = () => {
		if (orientationSettleTimer !== null) clearTimeout(orientationSettleTimer);
		orientationSettleTimer = setTimeout(() => {
			orientationSettleTimer = null;
			isLandscape.value = window.matchMedia(landscapeQuery).matches;
		}, 100);
	};
	landscapeMediaQuery.addEventListener?.("change", onOrientationChange);
	window.addEventListener("orientationchange", onOrientationChange);
	onBeforeUnmount(() => {
		if (orientationSettleTimer !== null) clearTimeout(orientationSettleTimer);
		landscapeMediaQuery.removeEventListener?.("change", onOrientationChange);
		window.removeEventListener("orientationchange", onOrientationChange);
	});

	// Also bound to @keydown.esc (LSP-17844): `persistent` means outside-click AND Escape won't
	// dismiss it, matching Vuetify's own `persistent` prop semantics.
	const requestClose = () => {
		if (props.persistent) return;
		emit("update:modelValue", false);
	};

	// Visibility is parent-controlled. The listener must still EXIST: Vuetify's useProxiedModel only
	// treats the model as controlled when BOTH the prop and an `onUpdate:modelValue` listener are
	// present; without it, VDialog would track internal state and could close itself without the
	// parent's consent, desyncing prop and overlay (LSP-17253 break-it round 1). Closing goes
	// through requestClose instead, so this is an intentional no-op.
	const onOverlayModelValueUpdate = () => {};

	// `scrollTopOverride` exists for the reopen path below. `.dialog-body-container` sets
	// `scroll-behavior: smooth`, so writing `scrollTop = 0` starts an ANIMATED scroll rather than
	// jumping — per CSSOM-View a real browser may not have applied it by the time the next line
	// reads `scrollTop` back, which would compute both shadows from the PRE-reset offset. The
	// reopen path therefore passes the offset it just set (0) instead of re-reading it, so the
	// recompute is deterministic and cannot race the smooth-scroll animation. The scroll handler
	// passes nothing and keeps reading the live offset, which is what it wants.
	// happy-dom/jsdom don't model smooth-scroll timing at all, so no unit test can catch that
	// race — it only reproduces in a real browser.
	function updateScrollShadows(element: HTMLElement, scrollTopOverride?: number) {
		const scrollTop = scrollTopOverride ?? element.scrollTop;
		hasScrolledDown.value = scrollTop > 0;
		hasScrolledUp.value = scrollTop + element.clientHeight < element.scrollHeight;
	}

	function handleScroll(event: Event) {
		const element = event.target as HTMLElement;
		if (!element) return;
		updateScrollShadows(element);
	}

	// LSP-17253: iOS Safari / WebKit only synthesizes a `click` from a tap when the tapped element
	// (or a non-document ancestor) is "clickable" — it has a direct click listener or similar
	// interactive affordance. Vuetify's scrim is a plain <div> with no direct listener, and its
	// ClickOutside directive listens at the DOCUMENT level, which does not count for WebKit's
	// heuristic. Net effect on iPhones: tapping the scrim produced touch/pointer events but never a
	// `click`, so `click:outside` never fired, the dialog could not be dismissed by tapping outside,
	// and Vuetify's `v-overlay-scroll-blocked` scroll lock stayed engaged ("breaks scrolling").
	// Attaching ANY direct click listener to the scrim flips WebKit's clickability heuristic, the
	// tap synthesizes a real `click`, and Vuetify's existing click-outside path then handles the
	// close exactly once — so the listener is deliberately a no-op and no behavior changes on
	// platforms where tap-to-close already worked (verified against Playwright WebKit + Chromium
	// with touch emulation on Vuetify 3.12.6). The persistent guard in requestClose still applies
	// because the close still flows through `click:outside`.
	const enableWebkitTapClickSynthesisOnScrim = () => {
		const overlayElement = bodyContainer.value?.closest(".v-overlay");
		// No surrounding .v-overlay means this isn't a real mounted v-dialog (e.g. a stubbed test
		// environment) — there is no scrim to instrument, so there is nothing to do or diagnose.
		if (!overlayElement) return;
		const scrimElement = overlayElement.querySelector(":scope > .v-overlay__scrim");
		if (!scrimElement) {
			// Diagnostic per the silent-failure logging rule: a missing scrim means iOS outside-tap
			// close is broken again for this dialog. `scrim: false` consumers legitimately have none.
			if (attrs.scrim !== false) {
				console.warn("Dialog open: scrim element not found; iOS tap-outside close may not work", {
					title: props.title
				});
			}
			return;
		}
		// Re-adding the same function reference on reopen is a no-op per the DOM spec, and the
		// listener is garbage-collected with the scrim element itself — no cleanup needed.
		scrimElement.addEventListener("click", webkitScrimTapClickSynthesisNoop);
	};
	// Named no-op: its only job is existing as a DIRECT listener on the scrim so WebKit treats the
	// scrim as clickable and synthesizes `click` from taps (see comment above).
	const webkitScrimTapClickSynthesisNoop = () => {};

	// Generation token for the reopen watcher below. A plain `props.modelValue` re-check after the
	// await can't tell two DIFFERENT "true" invocations apart when the prop round-trips
	// true->false->true (etc.) multiple times before either one's nextTick resolves — both would see
	// the same final `true` value. Each true-transition claims the next generation number; only the
	// invocation whose captured generation still matches the latest one when it resumes is allowed
	// to act (LSP-17276).
	let reopenGeneration = 0;

	watch(
		() => props.modelValue,
		async (isOpen) => {
			if (!isOpen) return;
			const generation = ++reopenGeneration;
			// Do NOT gate on bodyContainer.value here: after a real close (once v-dialog's leave
			// transition finishes), the previous body unmounts and the ref goes null before this
			// callback runs. Requiring it truthy up front would bail out permanently, even though
			// v-dialog mounts a fresh container moments later — leaving the scroll position and
			// shadows stuck from the prior session (LSP-17276). Wait a tick for the (re)mount, then check.
			await nextTick();
			// Every early return below skips the scroll-shadow recompute silently. Each one exists
			// because a break-it round found a real race here; log one line per fail-condition so a
			// future recurrence is a log read rather than the same archaeology.
			// Superseded by a later reopen that also resolved to true? Let that one own it.
			if (generation !== reopenGeneration) {
				console.warn("Dialog reopen: superseded by a newer reopen; skipping scroll-shadow recompute", {
					generation,
					latestGeneration: reopenGeneration
				});
				return;
			}
			// If a subsequent close superseded this invocation while it was suspended on the tick
			// above, this stale invocation must not reset the scroll position or shadows out from
			// under the session that is now closing.
			if (!props.modelValue) {
				console.warn("Dialog reopen: dialog closed again while awaiting nextTick; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// Read the ref once into a local: the assignment below can itself trigger synchronous
			// side effects (e.g. a scroll listener reacting to the reset), and re-reading
			// bodyContainer.value afterward could see it go null.
			const container = bodyContainer.value;
			if (!container) {
				console.warn("Dialog reopen: body container ref was null after nextTick; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// The overlay (and its scrim) is mounted by now — make iOS taps on the scrim synthesize
			// real clicks so tap-outside close works (LSP-17253; see the function's comment).
			enableWebkitTapClickSynthesisOnScrim();
			container.scrollTop = 0;
			// The scrollTop write above is the last point this container could be detached out from
			// under us; don't trust a detached node's metrics for the recompute.
			if (!container.isConnected) {
				console.warn("Dialog reopen: body container detached during scroll reset; skipping scroll-shadow recompute", {
					generation
				});
				return;
			}
			// Recompute from the live DOM metrics of the reopened content rather than hardcoding both
			// flags to false — at scrollTop 0, overflowing content still needs its "more content
			// below" shadow (LSP-17276). Pass the offset we just set rather than re-reading it; see
			// updateScrollShadows for why (smooth-scroll race).
			updateScrollShadows(container, 0);
		},
		// `immediate` so a dialog mounted already open is treated as an open too: without it the scrim
		// never gets its WebKit click-synthesis listener (LSP-17253 break-it round 1) and the initial
		// scroll/shadow state is never computed. The handler's own guards make the immediate run safe
		// when the dialog mounts closed — it exits at the `if (!isOpen) return`.
		{ immediate: true }
	);

	defineExpose({ isLandscape, hasScrolledDown, hasScrolledUp });
</script>

<style scoped>
	.dialog-header,
	.dialog-footer {
		background-color: rgb(var(--v-theme-surface));
	}

	/* `split`: always push the two actions left/right when there's room. flex-wrap (set on
	   .dialog-footer in the template) plus this minimum gap is what decides "is there room": if both
	   buttons plus this gap don't fit on one line, they wrap onto two rows (each flush-left) with
	   adequate vertical spacing, instead of letting the horizontal gap collapse below a usable
	   minimum. Each declaration needs !important: justify-content to beat VDialog's own
	   `.v-dialog > .v-overlay__content > .v-card > .v-card-actions { justify-content: flex-end }`,
	   the gaps to beat the `ga-2` utility. */
	.dialog-footer.two-default-actions {
		justify-content: space-between !important;
		column-gap: 1.25rem !important;
		row-gap: 1.25rem !important;
		padding-inline: 1rem;
	}

	.dialog-header.shadow-bottom {
		box-shadow: 0 6px 6px -6px rgba(0, 0, 0, 0.2);
	}
	.dialog-footer.shadow-top {
		box-shadow: 0 -6px 6px -6px rgba(0, 0, 0, 0.2);
	}

	.dialog-body-container {
		max-height: 50vh;
		overflow-y: auto;
		scroll-behavior: smooth;
	}
	.landscape-dialog-body-container {
		max-height: 40vh;
	}
	.full-height .dialog-body-container {
		max-height: 90vh;
	}
	.full-height .landscape-dialog-body-container {
		max-height: 80vh;
	}

	.dialog-container--fullscreen {
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.dialog-container--fullscreen .dialog-body-container {
		flex: 1 1 auto;
		max-height: none;
	}

	.dialog-body-container
		:deep(.v-field.v-field--active:not(.v-field--error):not(.v-field--disabled) > .v-field__prepend-inner > .v-icon) {
		color: rgb(var(--v-theme-primary)) !important;
		opacity: 1 !important;
	}
</style>
